import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Grid,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Typography,
  message,
} from 'antd';
import { KeyOutlined, PlusOutlined } from '@ant-design/icons';
import api from '../../../api/client';
import { usePermissions } from '../../../context/permissionsContext';

const { Title } = Typography;

const Usuarios = () => {
  const { hasPermission } = usePermissions();
  const screens = Grid.useBreakpoint();
  const isNarrow = screens.md === false;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  const [tipos, setTipos] = useState([]);
  const [rolesCatalogo, setRolesCatalogo] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalPassOpen, setModalPassOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [passUser, setPassUser] = useState(null);
  const [form] = Form.useForm();
  const [formPass] = Form.useForm();

  const loadRoles = useCallback(async () => {
    try {
      const { data } = await api.get('/roles');
      setRolesCatalogo(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar roles');
    }
  }, []);

  const loadTipos = useCallback(async () => {
    try {
      const { data: res } = await api.get('/usuarios/tipos');
      setTipos(res.items || []);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar tipos de usuario');
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/usuarios', {
        params: { page, limit, search: searchDebounced || undefined },
      });
      setData(res.items || []);
      setTotal(res.total ?? 0);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchDebounced]);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    loadTipos();
    loadRoles();
  }, [loadTipos, loadRoles]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    const usuarioTipo = tipos.find((t) => String(t.nombre).toUpperCase() === 'USUARIO');
    form.setFieldsValue({
      activo: true,
      ...(usuarioTipo ? { tipoUsuarioId: usuarioTipo.id } : {}),
    });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      tipoUsuarioId: row.tipoUsuarioId,
      activo: row.activo,
      roleIds: row.roleIds || [],
    });
    setModalOpen(true);
  };

  const openPassword = (row) => {
    setPassUser(row);
    formPass.resetFields();
    setModalPassOpen(true);
  };

  const submit = async () => {
    try {
      const v = await form.validateFields();
      if (editing) {
        await api.put(`/usuarios/${editing.id}`, {
          tipoUsuarioId: v.tipoUsuarioId,
          activo: v.activo,
          roleIds: v.roleIds || [],
        });
        message.success('Usuario actualizado');
      } else {
        await api.post('/usuarios', {
          usuario: v.usuario.trim(),
          password: v.password,
          tipoUsuarioId: v.tipoUsuarioId,
          activo: v.activo !== false,
          roleIds: v.roleIds || [],
        });
        message.success('Usuario creado');
      }
      setModalOpen(false);
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'Error al guardar');
    }
  };

  const submitPassword = async () => {
    try {
      const v = await formPass.validateFields();
      if (v.password !== v.password2) {
        message.error('Las contraseñas no coinciden');
        return;
      }
      await api.patch(`/usuarios/${passUser.id}/password`, {
        password: v.password,
      });
      message.success('Contraseña actualizada');
      setModalPassOpen(false);
      setPassUser(null);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'Error al actualizar contraseña');
    }
  };

  const columns = [
    {
      title: 'Usuario',
      dataIndex: 'usuario',
      ellipsis: true,
    },
    {
      title: 'Tipo',
      dataIndex: 'tipoUsuarioNombre',
      width: 140,
      render: (t) => t || '—',
    },
    {
      title: 'Roles',
      dataIndex: 'roles',
      ellipsis: true,
      render: (roles) =>
        Array.isArray(roles) && roles.length
          ? roles.map((r) => r.rol_nombre).join(', ')
          : '—',
    },
    {
      title: 'Activo',
      dataIndex: 'activo',
      width: 100,
      align: 'center',
      render: (v) => (v ? 'Si' : 'No'),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: isNarrow ? 120 : 200,
      fixed: 'right',
      render: (_, row) => (
        <Space size={0} wrap>
          {hasPermission('usuarios', 'editar') && (
            <Button type="link" size="small" onClick={() => openEdit(row)}>
              Editar
            </Button>
          )}
          {hasPermission('usuarios', 'editar') && (
            <Button type="link" size="small" icon={<KeyOutlined />} onClick={() => openPassword(row)}>
              Contraseña
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={<Title level={5}>Usuarios</Title>}
      extra={
        hasPermission('usuarios', 'crear') ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nuevo usuario
          </Button>
        ) : null
      }
      style={{ borderColor: '#d8e8dc', background: '#ffffff' }}
      styles={{ body: { padding: isNarrow ? 12 : 16 } }}
    >
      <div style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="Buscar por nombre de usuario"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
        />
      </div>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          showSizeChanger: false,
          onChange: (p) => setPage(p),
          size: 'small',
        }}
        scroll={{ x: 520 }}
        size="small"
      />

      <Modal
        title={editing ? 'Editar usuario' : 'Nuevo usuario'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        destroyOnClose
        okText="Guardar"
        width={isNarrow ? 'calc(100vw - 16px)' : 440}
      >
        <Form form={form} layout="vertical" requiredMark={false} style={{ marginTop: 8 }}>
          {!editing && (
            <>
              <Form.Item name="usuario" label="Usuario" rules={[{ required: true, message: 'Requerido' }]}>
                <Input placeholder="Nombre de acceso" autoComplete="off" />
              </Form.Item>
              <Form.Item
                name="password"
                label="Contraseña"
                rules={[
                  { required: true, message: 'Requerido' },
                  { min: 6, message: 'Minimo 6 caracteres' },
                ]}
              >
                <Input.Password placeholder="Minimo 6 caracteres" autoComplete="new-password" />
              </Form.Item>
            </>
          )}
          <Form.Item name="tipoUsuarioId" label="Tipo" rules={[{ required: true, message: 'Requerido' }]}>
            <Select
              placeholder="Seleccionar"
              options={tipos.map((t) => ({ value: t.id, label: t.nombre }))}
            />
          </Form.Item>
          <Form.Item name="activo" label="Activo" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="roleIds" label="Roles">
            <Select
              mode="multiple"
              allowClear
              placeholder="Seleccionar roles"
              options={rolesCatalogo.map((r) => ({
                value: r.rol_id,
                label: r.rol_nombre,
              }))}
            />
          </Form.Item>
          <Alert
            type="info"
            showIcon
            message="Los roles definen qué módulos puede ver cada usuario. El tipo Administrador/Usuario controla el acceso al panel; los permisos finos se asignan por rol."
          />
        </Form>
      </Modal>

      <Modal
        title={`Nueva contraseña — ${passUser?.usuario || ''}`}
        open={modalPassOpen}
        onCancel={() => {
          setModalPassOpen(false);
          setPassUser(null);
        }}
        onOk={submitPassword}
        destroyOnClose
        okText="Guardar"
        width={isNarrow ? 'calc(100vw - 16px)' : 400}
      >
        <Form form={formPass} layout="vertical" requiredMark={false} style={{ marginTop: 8 }}>
          <Form.Item
            name="password"
            label="Nueva contraseña"
            rules={[
              { required: true, message: 'Requerido' },
              { min: 6, message: 'Minimo 6 caracteres' },
            ]}
          >
            <Input.Password placeholder="Minimo 6 caracteres" autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="password2"
            label="Repetir contraseña"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Input.Password placeholder="Repetir" autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Usuarios;
